import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { ArrowRight, FileText, Eye } from "lucide-react";

/**
 * Public, unauthenticated reading view for a shared draft.
 * Privacy: the API returns ONLY {text, share_id, created_at, view_count} —
 * never the author's user_id, email, persona, or any other identifier.
 */
export default function SharedView() {
  const { shareId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const { data } = await api.get(`/share/${shareId}`);
        if (m) setData(data);
      } catch (e) {
        if (m) setError(e.response?.status === 404 ? "This share doesn't exist or was removed." : "Could not load.");
      } finally {
        if (m) setLoading(false);
      }
    })();
    return () => { m = false; };
  }, [shareId]);

  return (
    <div className="min-h-screen bg-obsidian text-white flex flex-col">
      <header className="border-b border-border bg-obsidian/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => nav("/")} data-testid="shared-logo-link"><Logo size={30} showTagline={false} /></button>
          <Button
            data-testid="shared-cta-top"
            onClick={() => nav("/")}
            className="bg-teal hover:bg-teal-hover text-white rounded-sm"
            size="sm"
          >
            Try Free for 3 Days <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-12 sm:py-16">
        {loading && (
          <div className="text-white/40 font-mono text-sm sr-blink" data-testid="shared-loading">Decrypting share…</div>
        )}

        {error && (
          <div className="sr-card p-8 text-center" data-testid="shared-error">
            <FileText className="h-6 w-6 mx-auto text-white/40 mb-3" />
            <p className="text-white/70">{error}</p>
            <Button onClick={() => nav("/")} className="mt-6 bg-teal hover:bg-teal-hover text-white rounded-sm">
              Back to SaiResponse
            </Button>
          </div>
        )}

        {data && (
          <article className="sr-fade-in" data-testid="shared-content">
            <div className="sr-pill text-teal mb-5"><span className="h-1.5 w-1.5 rounded-full bg-teal" /> Shared Draft</div>
            <div className="flex items-baseline justify-between mb-8 gap-4">
              <h1 className="font-display text-2xl sm:text-3xl tracking-tight text-white">A draft shared with you</h1>
              <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/40 flex items-center gap-1.5 shrink-0">
                <Eye className="h-3 w-3" /> {data.view_count} view{data.view_count === 1 ? "" : "s"}
              </span>
            </div>
            <div
              data-testid="shared-text"
              className="text-[15px] leading-[1.7] text-white/90 whitespace-pre-wrap font-sans"
            >
              {data.text}
            </div>

            {/* High-conversion CTA */}
            <div className="mt-12 p-7 sr-card sr-grain text-center sr-teal-glow" data-testid="shared-cta-block">
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-teal mb-3">Drafted with SaiResponse</div>
              <h2 className="font-display text-xl text-white tracking-tight">
                Generate calm, leadership-grade communications in seconds.
              </h2>
              <p className="text-sm text-white/55 mt-2 max-w-md mx-auto">
                Three personas. Nine modes. Zero retention. Try the smart agent interface trusted by support directors and account executives.
              </p>
              <Button
                data-testid="shared-cta-primary"
                onClick={async () => {
                  try { await api.post(`/share/${shareId}/click`); } catch {}
                  nav("/");
                }}
                className="mt-6 bg-teal hover:bg-teal-hover text-white rounded-sm px-6 h-11"
              >
                Drafted with SaiResponse · Try Free for 3 Days <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.16em] text-white/30">
                3-day frictionless trial · No card required
              </div>
            </div>
          </article>
        )}
      </main>

      <footer className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between gap-4 text-xs text-white/40 font-mono">
          <span>© 2026 SaiResponse</span>
          <span>Privacy by design · Zero retention</span>
        </div>
      </footer>
    </div>
  );
}
